import React from 'react';
import { observer } from 'mobx-react';

import { reverse } from 'named-urls';

import { Button } from 'components/Button';

import CustomTextInputField from 'components/SharedFormComponents/CustomTextInputField';
import CustomModal from 'components/CustomModal';

import { useStore } from 'utils/hooks/store';

import SiteDetailsStore from 'stores/SiteDetails';
import { getEntityTranslation } from 'stores/EntityDetails/utils';

import routes from 'core/routes';

import { EntityModules, RegisterEntityModule } from 'core/constants';

import { useDefaultFolderId } from 'containers/Documents/hooks/useDefaultFolderId';
import FolderTreePicker from 'containers/Documents/components/FolderTreePicker';
import { StyledPickerPath } from 'containers/Documents/components/FolderTreePicker/styles';
import { PathValue } from 'containers/Documents/components/MoveToFolderModal/styles';

import UnitPickerField from 'containers/OrganisationStructure/components/UnitPickerField';

import { DetailsRowContainer, TitleText, RequiredMark } from './styles';
import { EntitiesStores } from '../types';

const AddEntityModal: React.FunctionComponent<{
  onClose: (created: boolean) => void;
  store: EntitiesStores;
}> = ({ onClose, store }): React.ReactElement => {
  const { notification, routing, folders, session, organisationStructure } = useStore();
  const defaultFolderId = useDefaultFolderId();

  const { singular } = getEntityTranslation[store.entityType];

  const [name, setName] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const submittingRef = React.useRef(false);
  const [parentFolderId, setParentFolderId] = React.useState<string | null>(defaultFolderId);
  const [unitUuid, setUnitUuid] = React.useState<string | null>(null);

  // A Site carries the unit that decides who can see it, once the organisation is switched on.
  const needsUnit = store.entityType === EntityModules.Sites && !!session.user?.scopedPermissions;

  React.useEffect(() => {
    if (needsUnit) organisationStructure.loadUnits(false);
  }, [needsUnit, organisationStructure]);

  const handleCreate = async (): Promise<void> => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    const created =
      store instanceof SiteDetailsStore
        ? await store.create(name, null, unitUuid)
        : await store.create(name, parentFolderId || null);

    if (typeof created === 'string') {
      notification.enqueueSuccessSnackbar(`${singular} created successfully`);
      routing.push(reverse(routes.dashboard[store.entityType as RegisterEntityModule].details, { entityId: created }));
      onClose(true);
    } else {
      const reason = store instanceof SiteDetailsStore ? store.createError : undefined;
      notification.enqueueErrorSnackbar(reason || `The ${singular} could not be created. Something went wrong.`);
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const onCloseModal = (): void => onClose(false);

  return (
    <CustomModal
      openModal
      onClose={onCloseModal}
      modalTitle={`Create New ${singular}`}
      cancelButton={
        <Button id="cancel-button" onClick={onCloseModal} variant="outlined" size="large" data-testid="cancelButton">
          Cancel
        </Button>
      }
      submitButton={
        <Button
          id={`create-${store.entityType}-button`}
          onClick={handleCreate}
          disabled={
            name === '' ||
            submitting ||
            (store.entityType === EntityModules.Documents && !parentFolderId) ||
            (needsUnit && !unitUuid)
          }
          variant="contained"
          size="large"
          data-testid="addButton"
        >
          Create
        </Button>
      }
    >
      <DetailsRowContainer>
        <TitleText>
          {singular} Name <RequiredMark>*</RequiredMark>
        </TitleText>
        <CustomTextInputField
          value={name}
          onChange={(e) => setName((e as unknown as string).trim())}
          description="Name"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleCreate();
            }
          }}
        />
      </DetailsRowContainer>
      {needsUnit && (
        <DetailsRowContainer>
          <TitleText as="label" htmlFor="new-site-unit">
            Unit <RequiredMark>*</RequiredMark>
          </TitleText>
          <UnitPickerField
            id="new-site-unit"
            units={organisationStructure.tree}
            value={unitUuid}
            onChange={setUnitUuid}
            placeholder="Select a unit"
            label="Unit"
          />
        </DetailsRowContainer>
      )}
      {store.entityType === EntityModules.Documents && (
        <DetailsRowContainer>
          <TitleText>
            Parent folder <RequiredMark>*</RequiredMark>
          </TitleText>
          <FolderTreePicker folders={folders.folders} selectedFolderId={parentFolderId} onSelect={setParentFolderId} />
          {parentFolderId && (
            <StyledPickerPath>
              Parent folder: <PathValue>{folders.getFolderPath(parentFolderId)}</PathValue>
            </StyledPickerPath>
          )}
        </DetailsRowContainer>
      )}
    </CustomModal>
  );
};

export default observer(AddEntityModal);
